import { useMemo, useState } from "react";
import moment from "moment/moment";
import { IconSend } from "@tabler/icons-react";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils/index.jsx";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "../UI/DocumentThumbnail";
import type { DownloadableFile } from "../../hooks/use-document-download";
import styles from "./CrgGenerateForm.module.css";

interface MonthOption {
  value: string;
  label: string;
}

interface CrgGenerateFormProps {
  profileBailleurId: number;
  /** Destinataire proposé pour l'envoi par email (adresse du profil bailleur). */
  defaultMail?: string;
  onClose: () => void;
}

export default function CrgGenerateForm({
  profileBailleurId,
  defaultMail = "",
  onClose,
}: CrgGenerateFormProps) {
  const axios = useAuthenticatedAxios();
  const [document, setDocument] = useState<
    (DownloadableFile & { id: number }) | null
  >(null);
  const [error, setError] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(null);
  const [mail, setMail] = useState(defaultMail);
  const [sentTo, setSentTo] = useState("");
  const [sendError, setSendError] = useState("");

  const currentMonth = moment().format("MM/YYYY");

  const monthsList = useMemo(() => {
    const months: MonthOption[] = [];
    for (let i = 0; i <= 12; i++) {
      const monthMoment = moment().subtract(i, "months");
      const label = monthMoment.format("MMMM YYYY");
      const value = monthMoment.format("MM/YYYY");
      months.push({
        value,
        label:
          label.charAt(0).toUpperCase() +
          label.slice(1) +
          (value === currentMonth ? " (mois en cours)" : ""),
      });
    }
    return months;
  }, [currentMonth]);

  const isCurrentMonth = selectedMonth?.value === currentMonth;

  const [isLoading, handleGenerate] = useLoader(async () => {
    if (!selectedMonth) return;
    try {
      setError("");
      const response = await axios.post(`/profiles/${profileBailleurId}/crg`, {
        month: selectedMonth.value,
      });
      setDocument(response.data);
    } catch (err) {
      setError(handleAPIError(err));
      throw err;
    }
  });

  const [isSending, handleSend] = useLoader(async () => {
    if (!document || !selectedMonth) return;
    try {
      setSendError("");
      await axios.post(
        `/profiles/${profileBailleurId}/crg/${document.id}/send-mail`,
        { mail: mail.trim(), month: selectedMonth.value },
      );
      setSentTo(mail.trim());
    } catch (err) {
      setSendError(handleAPIError(err));
      throw err;
    }
  });

  if (document) {
    return (
      <div className={styles.container}>
        <Text weight="bold">Compte rendu de gestion</Text>
        <Callout variant="success" title="CRG généré">
          Le compte rendu de gestion {selectedMonth?.label} est disponible dans
          l'onglet Documents du profil.
        </Callout>
        <div className={styles.preview}>
          <DocumentDownloadLink documentId={document.id} file={document}>
            <DocumentThumbnail documentId={document.id} file={document} />
          </DocumentDownloadLink>
        </div>
        <div className={styles.sendBlock}>
          <Text weight="bold">Envoyer le CRG par email</Text>
          <div className={styles.sendRow}>
            <TextInputStandalone
              type="email"
              name="crg-mail"
              label="Adresse email du destinataire"
              value={mail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMail(e.target.value)
              }
              placeholder="destinataire@exemple.fr"
              autoComplete="email"
              className={styles.mailInput}
            />
            <Button
              type="button"
              variant="secondary"
              icon={IconSend}
              onClick={handleSend}
              isLoading={isSending}
              disabled={isSending || !mail.trim()}
            >
              Envoyer par email
            </Button>
          </div>
          <Callout variant="danger">{sendError}</Callout>
          <Callout variant="success">
            {sentTo && `Le compte rendu de gestion a été envoyé à ${sentTo}.`}
          </Callout>
        </div>
        <div className={styles.actions}>
          <Button type="button" variant="primary" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Text weight="bold">Générer un compte rendu de gestion</Text>
      <ReactSelectStandalone
        label="Sélectionnez le mois pour lequel vous souhaitez générer le CRG"
        options={monthsList}
        value={selectedMonth}
        onChange={setSelectedMonth}
        placeholder="Sélectionner un mois"
        isClearable={false}
      />
      <Callout variant="info">
        {isCurrentMonth &&
          "Le mois n'est pas terminé : le compte rendu reprendra les loyers, règlements et dépenses enregistrés à ce jour. Les opérations saisies après la génération n'y figureront pas."}
      </Callout>
      <Callout variant="danger">{error}</Callout>
      <div className={styles.actions}>
        <Button type="button" variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleGenerate}
          isLoading={isLoading}
          disabled={isLoading || !selectedMonth}
        >
          Générer
        </Button>
      </div>
    </div>
  );
}
